import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, alpha, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { useFuzzScanTarget } from 'context/FuzzScanTargetContext';
import { listSecrets, listUserScans, runSecretsHistoryScan, updateSecret } from 'api/features';
import { GREEN, MONO, SEV_COLOR, ago } from './scanUi';
import { ordered, patternsFrom, repoOf, scanLine, summary, typeOf } from './secretsLogic';

const panel = { borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper' };
const fail = (e, fb) => e?.response?.data?.detail || e?.message || fb;
const Pill = ({ color, children }) => <Typography component="span" sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.4, px: 1.3, py: 0.5, borderRadius: 99, bgcolor: alpha(color, 0.16), color, whiteSpace: 'nowrap' }}>{children}</Typography>;

export default function SecretsDetectionPage() {
  const { currentTargetId, targets, setCurrentTargetId } = useFuzzScanTarget();
  const [rows, setRows] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [form, setForm] = useState({ url: '', history: true, advanced: false, patterns: '' });
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = currentTargetId ? { user_target: currentTargetId } : {};
      const [s, sc] = await Promise.all([listSecrets(params), listUserScans(params)]);
      setRows(Array.isArray(s) ? s : s.results || []);
      setScans((Array.isArray(sc) ? sc : sc.results || []).filter((x) => x.scan_type === 'secrets_git_history').slice(0, 4));
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not load your secrets.') }); }
    setLoading(false);
  }, [currentTargetId]);
  useEffect(() => { load(); }, [load]);

  const running = scans.some((x) => x.status === 'pending' || x.status === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const mark = async (s, status) => {
    setBusy(s.id);
    try { await updateSecret(s.id, { status }); setToast({ severity: 'success', message: status === 'rotated' ? 'Marked as rotated.' : 'Marked as not a secret.' }); await load(); } catch (e) { setToast({ severity: 'error', message: fail(e, 'That did not work.') }); }
    setBusy(null);
  };
  const scan = async () => {
    setLaunching(true);
    try {
      await runSecretsHistoryScan({ user_target: currentTargetId, repo_path: form.url.trim(), include_git_history: form.history, custom_patterns: patternsFrom(form.patterns) });
      setToast({ severity: 'success', message: 'Scan started. Results appear here as it finishes.' });
      await load();
    } catch (e) { setToast({ severity: 'error', message: fail(e, 'Could not start the scan.') }); }
    setLaunching(false);
  };

  const list = useMemo(() => ordered(rows), [rows]);
  const sum = useMemo(() => summary(rows), [rows]);
  const stat = (label, value, sub, color) => (
    <Box sx={{ ...panel, p: 2.2, flex: '1 1 190px' }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, fontFamily: MONO, color }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{sub}</Typography>
    </Box>
  );

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress sx={{ color: GREEN }} /></Box>;
  return (
    <Stack spacing={2.5} className="fuzzscan-page" sx={{ p: 3 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Secrets detection</Typography>
        <Typography color="text.secondary" variant="body2">Leaked passwords and keys found in your code, with what to do about each one</Typography>
      </Box>
      <Stack direction="row" gap={2} flexWrap="wrap">
        {stat('Still active', sum.active, sum.active ? 'rotate these first' : 'nothing to fix', sum.active ? '#ef4444' : GREEN)}
        {stat('Rotated', sum.rotated, 'fixed', GREEN)}
        {stat('Repositories scanned', sum.repos, scans[0] ? `last scan ${ago(scans[0].created_at)}` : 'no scan yet')}
      </Stack>

      <Stack spacing={1.5}>
        {!list.length && <Box sx={{ ...panel, p: 3 }}><Typography color="text.secondary">No secrets found yet. Scan a repository below.</Typography></Box>}
        {list.map((s) => {
          const t = typeOf(s);
          const done = s.status !== 'active';
          const sev = SEV_COLOR[s.severity] || '#64748b';
          return (
            <Box key={s.id} sx={{ ...panel, p: 2.6, opacity: done ? 0.6 : 1, borderColor: !done && s.severity === 'critical' ? alpha('#ef4444', 0.45) : 'divider' }}>
              <Stack direction="row" alignItems="center" gap={2.5} flexWrap="wrap">
                <Box sx={{ flex: '2 1 300px', minWidth: 0 }}>
                  <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 0.6 }}>
                    <Pill color={sev}>{s.severity.toUpperCase()}</Pill>
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{t.label}</Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary"><span style={{ fontFamily: MONO }}>{s.location || s.file_path}</span> in {repoOf(s)}</Typography>
                </Box>
                <Typography sx={{ fontFamily: MONO, fontSize: 13 }} color="text.secondary">{s.masked_snippet || 'hidden'}</Typography>
                <Pill color={done ? GREEN : '#ef4444'}>{s.status === 'active' ? 'Active' : s.status === 'rotated' ? 'Rotated' : 'Not a secret'}</Pill>
                {!done && (
                  <Stack direction="row" gap={0.8}>
                    <Button size="small" variant="contained" color="success" disabled={busy === s.id} onClick={() => mark(s, 'rotated')}>Mark rotated</Button>
                    <Button size="small" variant="outlined" color="inherit" disabled={busy === s.id} onClick={() => mark(s, 'false_positive')}>Not a secret</Button>
                  </Stack>
                )}
              </Stack>
              {!done && (
                <Box sx={{ mt: 1.4 }}>
                  <Button size="small" color="inherit" onClick={() => setOpen({ ...open, [s.id]: !open[s.id] })} sx={{ px: 0, textTransform: 'none', fontWeight: 600 }}>{open[s.id] ? 'Hide the steps' : 'What to do'}</Button>
                  {open[s.id] && (
                    <Box component="ol" sx={{ m: 0, mt: 0.6, pl: 2.6, color: 'text.secondary', lineHeight: 1.8, fontSize: 14 }}>
                      {t.steps.map((step) => <li key={step}>{step}</li>)}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      <Box sx={{ ...panel, p: 3, background: `linear-gradient(135deg, ${alpha(GREEN, 0.1)}, transparent 60%)` }}>
        <Typography variant="overline" color="text.secondary">Scan a repository</Typography>
        <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mt: 1 }}>
          {(targets || []).length > 1 && (
            <TextField select size="small" label="Workspace" value={currentTargetId || ''} onChange={(e) => setCurrentTargetId(e.target.value)} sx={{ minWidth: 200 }}>
              {targets.map((t) => <MenuItem key={t.id} value={t.id}>{t.name || t.target?.value}</MenuItem>)}
            </TextField>
          )}
          <TextField size="small" placeholder="https://github.com/acme/web-app.git" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} sx={{ flex: '1 1 340px', '& input': { fontFamily: MONO } }} />
          <Button variant="contained" disabled={launching || !form.url.trim() || !currentTargetId} onClick={scan} sx={{ fontWeight: 700, bgcolor: GREEN, color: '#06220F', '&:hover': { bgcolor: '#16a34a' } }}>Scan now</Button>
        </Stack>
        <Stack direction="row" gap={2.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
          <FormControlLabel control={<Checkbox size="small" checked={form.history} onChange={(e) => setForm({ ...form, history: e.target.checked })} sx={{ '&.Mui-checked': { color: GREEN } }} />} label="Include old versions (git history)" />
          <Typography variant="body2" color="text.secondary">Public https repositories only</Typography>
          <Button size="small" color="inherit" onClick={() => setForm({ ...form, advanced: !form.advanced })} sx={{ textTransform: 'none' }}>Advanced: your own patterns</Button>
        </Stack>
        {form.advanced && <TextField fullWidth multiline minRows={3} size="small" sx={{ mt: 1, '& textarea': { fontFamily: MONO } }} label="One pattern per line (regular expressions, 200 characters at most)" value={form.patterns} onChange={(e) => setForm({ ...form, patterns: e.target.value })} />}
        {scans.length > 0 && (
          <Stack spacing={0.6} sx={{ mt: 2 }}>
            {scans.map((x) => (
              <Stack key={x.id} direction="row" gap={2} alignItems="center">
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: x.status === 'failed' ? '#ef4444' : x.status === 'completed' ? GREEN : '#38bdf8' }} />
                <Typography variant="body2" sx={{ flex: 1 }} color={x.status === 'failed' ? 'error' : 'text.secondary'}>{scanLine(x)}</Typography>
                <Typography variant="caption" color="text.secondary">{ago(x.created_at)}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary">We only show a hidden version of each secret, never the real value. The repository is downloaded for the scan and deleted straight after.</Typography>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>{toast ? <Alert severity={toast.severity} variant="filled">{toast.message}</Alert> : undefined}</Snackbar>
    </Stack>
  );
}
